import { create } from 'zustand';
import financialRecordService from '../services/financialRecordService';
import { 
  FinancialRecord, 
  FinancialRecordFilter, 
  FinancialRecordsResponse,
  FinancialSummary 
} from '../types/data/financialRecord';

interface FinancialRecordState {
  records: FinancialRecord[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  filter: FinancialRecordFilter | null;
  summary: FinancialSummary | null;
  selectedRecordId: string | null;
  lastUpdated: Date;
  
  // Actions
  fetchRecords: (
    page?: number, 
    pageSize?: number, 
    filter?: FinancialRecordFilter | null
  ) => Promise<void>;
  
  fetchRecordsQuietly: (
    page?: number, 
    pageSize?: number, 
    filter?: FinancialRecordFilter | null
  ) => Promise<void>;
  
  fetchRecordById: (id: string) => Promise<FinancialRecord | null>;
  createRecord: (record: Omit<FinancialRecord, 'id' | 'createdAt' | 'updatedAt'>) => Promise<FinancialRecord>;
  updateRecord: (id: string, updates: Partial<FinancialRecord>) => Promise<FinancialRecord | null>;
  deleteRecord: (id: string) => Promise<boolean>;
  setFilter: (filter: FinancialRecordFilter | null) => void;
  setSelectedRecordId: (id: string | null) => void;
  resetState: () => void;
}

const useFinancialRecordStore = create<FinancialRecordState>((set, get) => ({
  // Initial state
  records: [],
  isLoading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
  filter: null,
  summary: null,
  selectedRecordId: null,
  lastUpdated: new Date(),
  
  // Fetch records with pagination and filtering
  fetchRecords: async (page = 1, pageSize = get().pageSize, filter = get().filter) => {
    try {
      // Only update pageSize if it changed to prevent unnecessary re-renders
      const currentPageSize = get().pageSize;
      const stateUpdate: Partial<FinancialRecordState> = {
        isLoading: true, 
        error: null,
        filter: filter,
        currentPage: page
      };
      
      // Only add pageSize to the state update if it's different
      if (pageSize !== currentPageSize) {
        stateUpdate.pageSize = pageSize;
      }
      
      // Do a single state update for all initial state changes to reduce re-renders
      set(stateUpdate);
      
      const response = await financialRecordService.getFinancialRecords(
        filter || undefined,
        page,
        pageSize,
        true // Always calculate summary
      );
      
      // Do a single state update with all results to avoid multiple re-renders
      set({ 
        records: response.records,
        totalCount: response.totalCount,
        summary: response.summary || null,
        isLoading: false,
        lastUpdated: new Date()
      });
    } catch (error) {
      console.error('Error fetching financial records:', error);
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Failed to fetch financial records' 
      });
    }
  },
  
  // Fetch records quietly (without showing loading state) - for background auto-refresh
  fetchRecordsQuietly: async (page = get().currentPage, pageSize = get().pageSize, filter = get().filter) => {
    try {
      // Don't set isLoading to true to avoid UI flickering
      set({ error: null });
      
      const response = await financialRecordService.getFinancialRecords(
        filter || undefined,
        page,
        pageSize,
        true // Always calculate summary
      );
      
      set({ 
        records: response.records,
        totalCount: response.totalCount,
        summary: response.summary || null,
        currentPage: page,
        pageSize,
        filter: filter,
        lastUpdated: new Date()
      });
    } catch (error) {
      console.error('Error quietly fetching financial records:', error);
      // Don't set error state to avoid disrupting user
    }
  },
  
  // Fetch a single record by ID
  fetchRecordById: async (id: string) => {
    try {
      set({ isLoading: true, error: null });
      
      const record = await financialRecordService.getFinancialRecordById(id);
      
      if (record) {
        set({ selectedRecordId: record.id });
      }
      
      set({ isLoading: false });
      return record;
    } catch (error) {
      console.error(`Error fetching financial record with ID ${id}:`, error);
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : `Failed to fetch financial record with ID ${id}` 
      });
      return null;
    }
  },
  
  // Create a new financial record
  createRecord: async (recordData) => {
    try {
      set({ isLoading: true, error: null });
      
      const newRecord = await financialRecordService.createFinancialRecord(recordData);
      
      // Update state with new record
      set(state => ({
        records: [newRecord, ...state.records],
        totalCount: state.totalCount + 1,
        isLoading: false,
        lastUpdated: new Date()
      }));
      
      return newRecord;
    } catch (error) {
      console.error('Error creating financial record:', error);
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Failed to create financial record' 
      });
      throw error;
    }
  },
  
  // Update an existing financial record
  updateRecord: async (id, updates) => {
    try {
      // Keep track of original records to restore if needed
      const originalRecords = [...get().records];
      
      // Optimistic update with minimal state changes to avoid UI reset
      // Important: we avoid setting isLoading: true which can trigger UI refreshes
      set(state => ({
        error: null,
        // Only update the specific record that changed
        records: state.records.map(r => 
          r.id === id ? { ...r, ...updates, updatedAt: new Date().toISOString() } : r
        )
      }));
      
      // Notify that an update is in progress through isLoading, but don't re-render the data
      // This creates a separate state update so it doesn't trigger the data rendering
      set({ isLoading: true });
      
      const updatedRecord = await financialRecordService.updateFinancialRecord(id, updates);
      
      if (updatedRecord) {
        // Update with server data, but avoid full re-render
        set(state => {
          // Get the current records (which include our optimistic update)
          const currentRecords = state.records;
          
          // Replace just the updated record with the server response
          const newRecords = currentRecords.map(r => 
            r.id === updatedRecord.id ? updatedRecord : r
          );
          
          return {
            records: newRecords,
            isLoading: false,
            lastUpdated: new Date()
          };
        });
      } else {
        // If update failed, revert to original data
        set(state => ({
          records: originalRecords,
          isLoading: false
        }));
      }
      
      return updatedRecord;
    } catch (error) {
      console.error(`Error updating financial record with ID ${id}:`, error);
      // If there's an error, reset loading state without changing records
      // This avoids triggering scroll reset due to data changes
      set({ isLoading: false, error: error instanceof Error ? error.message : `Failed to update financial record with ID ${id}` });
      
      throw error;
    }
  },
  
  // Delete a financial record
  deleteRecord: async (id) => {
    try {
      // Optimistic UI update
      const originalRecords = [...get().records];
      set(state => ({
        isLoading: true, 
        error: null,
        records: state.records.filter(r => r.id !== id),
        totalCount: state.totalCount - 1
      }));
      
      const success = await financialRecordService.deleteFinancialRecord(id);
      
      if (success) {
        // Keep optimistic update and update other state
        set(state => ({
          selectedRecordId: state.selectedRecordId === id ? null : state.selectedRecordId,
          isLoading: false,
          lastUpdated: new Date()
        }));
        
        // Refresh summary
        if (get().summary) {
          // Refresh summary without changing the records (to avoid flicker)
          const tempResponse = await financialRecordService.getFinancialRecords(
            get().filter || undefined,
            get().currentPage,
            get().pageSize,
            true // Calculate summary
          );
          
          set({ summary: tempResponse.summary || null });
        }
      } else {
        // Revert optimistic update if delete failed
        set({ 
          records: originalRecords,
          totalCount: originalRecords.length,
          isLoading: false 
        });
      }
      
      return success;
    } catch (error) {
      console.error(`Error deleting financial record with ID ${id}:`, error);
      // Revert optimistic update
      get().fetchRecords(get().currentPage, get().pageSize);
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : `Failed to delete financial record with ID ${id}` 
      });
      return false;
    }
  },
  
  // Set the filter without automatically fetching records
  setFilter: (filter) => {
    set({ filter });
    // Removed automatic fetchRecords call that was causing refresh loops
  },
  
  // Set the selected record ID
  setSelectedRecordId: (id) => {
    set({ selectedRecordId: id });
  },
  
  // Reset state to initial values
  resetState: () => {
    set({
      records: [],
      isLoading: false,
      error: null,
      totalCount: 0,
      currentPage: 1,
      pageSize: 20,
      filter: null,
      summary: null,
      selectedRecordId: null,
      lastUpdated: new Date()
    });
  }
}));

export default useFinancialRecordStore; 